import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RepoBranchPicker } from "@/components/gitea/RepoBranchPicker";
import { useResetWorkspace } from "@/api/mutations/agents";
import type { Agent } from "@/types";

interface Props {
  agent: Agent;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// #215 (E3): Workspace neu aus einem Gitea-Repo@Branch forken (Owner-Content wird
// verworfen, Repo gewinnt). Vorbelegt mit der aktuellen Seed-Quelle des Agents —
// gleich lassen = Reset, anderes Repo/Branch wählen = Flash aus neuer Quelle.
export function FlashWorkspaceDialog({ agent, open, onOpenChange }: Props) {
  const [seedRepo, setSeedRepo] = useState<string | null>(agent.workspaceRepo?.seedRepo ?? null);
  const [seedBranch, setSeedBranch] = useState<string | null>(agent.workspaceRepo?.seedBranch ?? null);
  const [serverError, setServerError] = useState<string | null>(null);
  const mutation = useResetWorkspace();

  function handleClose(o: boolean) {
    if (!o) {
      setSeedRepo(agent.workspaceRepo?.seedRepo ?? null);
      setSeedBranch(agent.workspaceRepo?.seedBranch ?? null);
      setServerError(null);
    }
    onOpenChange(o);
  }

  function handleSubmit() {
    if (!seedRepo) return;
    setServerError(null);
    mutation.mutate(
      { id: agent.id, seed: { seedRepo, seedBranch } },
      {
        onSuccess: () => {
          toast.success(`Workspace von ${agent.id} aus ${seedRepo}@${seedBranch ?? "(default)"} geflasht`);
          handleClose(false);
        },
        onError: (err) => {
          const msg = (err as Error).message;
          setServerError(msg);
          toast.error(`Flash fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="flash-workspace-dialog">
        <DialogHeader>
          <DialogTitle>Workspace aus Gitea flashen</DialogTitle>
          <DialogDescription>
            Baut den Workspace neu aus dem gewählten Repo@Branch (Fork). Der bisherige
            Owner-Content (Gitea-Repo + workspace-data) wird verworfen — <strong>das Repo
            gewinnt</strong>. Gitea-Identität + claude-home (Settings/Plugins) bleiben.
            Läuft der Agent, wird er neu gestartet. NICHT umkehrbar.
          </DialogDescription>
        </DialogHeader>

        <RepoBranchPicker
          seedRepo={seedRepo}
          seedBranch={seedBranch}
          onChange={(r, b) => {
            setSeedRepo(r);
            setSeedBranch(b);
          }}
          allowNone={false}
        />

        {serverError && (
          <div className="text-sm text-red" data-testid="flash-server-error">
            {serverError}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} data-testid="flash-cancel">
            Abbrechen
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={!seedRepo || mutation.isPending}
            data-testid="flash-submit"
          >
            {mutation.isPending ? "..." : "Workspace flashen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
